import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Alert,
  Divider,
  Switch,
  Tabs,
  Spin,
  Progress,
  Typography,
  Tooltip,
  Grid,
  Radio,
  Popconfirm,
  Row,
  Col,
  Collapse
} from 'antd';
import {
  LockOutlined,
  SafetyCertificateOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  LoadingOutlined,
  UploadOutlined,
  FolderOpenOutlined,
  CloudServerOutlined,
  PlusOutlined,
  DeleteOutlined,
  CopyOutlined,
  DownloadOutlined,
  StopOutlined,
  EyeOutlined,
  MinusCircleOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';
import cdnService from '../../services/cdnService';
import TagsInput, { isOrigin, isHeaderName } from '../../../shared/components/TagsInput';
import { DurationInput } from '../../../shared/components/cdn/UnitInputs';
import CspBuilder from '../../../shared/components/cdn/CspBuilder';

// 签发进行中的状态:弹窗和列表在这些状态下自动刷新
const ACME_IN_PROGRESS = ['pending', 'processing', 'verifying', 'issuing'];

// CSP 仅报告模式:后端只有 security.csp,仅报告头走自定义 set 头(契约不变)
const CSP_RO = 'content-security-policy-report-only';

const { Text, Paragraph } = Typography;

// ACME CA for auto certificates (domains.acme_ca; null = Let's Encrypt)
const ACME_CAS = [
  { value: 'letsencrypt', label: "Let's Encrypt" },
  { value: 'zerossl', label: 'ZeroSSL' },
];
const caLabel = (v) => ACME_CAS.find((c) => c.value === v)?.label;
const { TextArea } = Input;
const { useBreakpoint } = Grid;

const ensureArray = (val) => {
  if (Array.isArray(val)) return val;
  if (typeof val === 'string') { try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; } catch { return []; } }
  return [];
};

// SSL Mode descriptions
const SSL_MODES = [
  {
    value: 'off',
    label: 'SSL 关闭',
    color: '#f5222d',
    description: '不使用加密，CDN 到源站使用 HTTP 明文连接。'
  },
  {
    value: 'flexible',
    label: '灵活',
    color: '#fa8c16',
    description: '客户端到 CDN 使用 HTTPS，CDN 到源站使用 HTTP。源站无需配置 SSL 证书。'
  },
  {
    value: 'full',
    label: '完全',
    color: '#1890ff',
    description: 'CDN 到源站使用 HTTPS，但不验证源站证书。源站可以使用自签名证书。'
  },
  {
    value: 'full_strict',
    label: '完全（严格）',
    color: '#52c41a',
    description: 'CDN 到源站使用 HTTPS 并验证证书有效性。源站需要使用由受信 CA 签发的证书，或使用平台 Origin CA 证书。'
  }
];

const linesJoin = (arr) => (Array.isArray(arr) ? arr.join('\n') : (arr || ''));
const linesToArr = (s) => (s || '').split('\n').map((x) => x.trim()).filter(Boolean);

const FRAME_OPTIONS = [
  { value: '', label: '不设置' },
  { value: 'SAMEORIGIN', label: 'SAMEORIGIN（仅同源可嵌入）' },
  { value: 'DENY', label: 'DENY（禁止任何嵌入）' },
];

const REFERRER_POLICIES = [
  { value: '', label: '不设置' },
  { value: 'no-referrer', label: 'no-referrer' },
  { value: 'no-referrer-when-downgrade', label: 'no-referrer-when-downgrade' },
  { value: 'origin', label: 'origin' },
  { value: 'origin-when-cross-origin', label: 'origin-when-cross-origin' },
  { value: 'same-origin', label: 'same-origin' },
  { value: 'strict-origin', label: 'strict-origin' },
  { value: 'strict-origin-when-cross-origin', label: 'strict-origin-when-cross-origin' },
  { value: 'unsafe-url', label: 'unsafe-url' },
];

const CORS_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];

const headersToForm = (headers) => {
  headers = headers || {};
  const hsts = headers.hsts || {};
  const sec = headers.security || {};
  const cors = headers.cors || {};
  const setList = Array.isArray(headers.set) ? headers.set : [];
  const roHeader = !sec.csp ? setList.find((h) => String(h?.name || '').toLowerCase() === CSP_RO) : null;
  return {
    hsts: {
      enabled: !!hsts.enabled,
      max_age: hsts.max_age ?? 31536000,
      include_sub_domains: !!hsts.include_sub_domains,
      preload: !!hsts.preload,
    },
    security: {
      enabled: !!sec.enabled,
      frame_options: sec.frame_options || '',
      content_type_options: !!sec.content_type_options,
      referrer_policy: sec.referrer_policy || '',
      csp: sec.csp || roHeader?.value || '',
      csp_report_only: !!roHeader,
    },
    cors: {
      enabled: !!cors.enabled,
      allow_origins: linesJoin(cors.allow_origins),
      allow_methods: ensureArray(cors.allow_methods),
      allow_headers: linesJoin(cors.allow_headers),
      expose_headers: linesJoin(cors.expose_headers),
      allow_credentials: !!cors.allow_credentials,
      max_age: cors.max_age ?? 0,
    },
    // 自定义响应头 set/add/remove → settings.security.headers.{set,add,remove}
    set: setList.filter((h) => h !== roHeader).map((h) => ({ name: h.name, value: h.value })),
    add: Array.isArray(headers.add) ? headers.add.map((h) => ({ name: h.name, value: h.value })) : [],
    remove: Array.isArray(headers.remove) ? headers.remove : [],
  };
};

const buildHeadersPayload = (v) => {
  v = v || {};
  const hsts = v.hsts || {};
  const sec = v.security || {};
  const cors = v.cors || {};
  const cspRo = !!(sec.enabled && sec.csp_report_only && (sec.csp || '').trim());
  const setOut = (v.set || []).filter((h) => h && h.name && String(h.name).toLowerCase() !== CSP_RO).map((h) => ({ name: h.name, value: h.value || '' }));
  if (cspRo) setOut.push({ name: 'Content-Security-Policy-Report-Only', value: sec.csp.trim() });
  return {
    hsts: {
      enabled: !!hsts.enabled,
      max_age: hsts.max_age || 0,
      include_sub_domains: !!hsts.include_sub_domains,
      preload: !!hsts.preload,
    },
    security: {
      enabled: !!sec.enabled,
      frame_options: sec.frame_options || '',
      content_type_options: !!sec.content_type_options,
      referrer_policy: sec.referrer_policy || '',
      csp: cspRo ? '' : (sec.csp || ''),
    },
    cors: {
      enabled: !!cors.enabled,
      allow_origins: linesToArr(cors.allow_origins),
      allow_methods: ensureArray(cors.allow_methods),
      allow_headers: linesToArr(cors.allow_headers),
      expose_headers: linesToArr(cors.expose_headers),
      allow_credentials: !!cors.allow_credentials,
      max_age: cors.max_age || 0,
    },
    // 自定义响应头 set/add/remove
    set: setOut,
    add: (v.add || []).filter((h) => h && h.name).map((h) => ({ name: h.name, value: h.value || '' })),
    remove: (v.remove || []).filter(Boolean),
  };
};

export default function SslSettings() {
  const { t } = useTranslation();
  const { domainData, reloadDomain } = useOutletContext();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [loading, setLoading] = useState(false);
  const [forceHttps, setForceHttps] = useState(false);
  const [acmeCa, setAcmeCa] = useState('letsencrypt');
  const [acmeCaSaving, setAcmeCaSaving] = useState(false);
  const [sslMode, setSslMode] = useState('full');
  const [sslModeLoading, setSslModeLoading] = useState(false);

  // Security response headers (HSTS / security headers / CORS) → settings.security.headers
  const [headersForm] = Form.useForm();
  const [headersSaving, setHeadersSaving] = useState(false);

  // Per-hostname SSL modal
  const [sslModalVisible, setSslModalVisible] = useState(false);
  const [sslHostname, setSslHostname] = useState(null);
  const [sslLoading, setSslLoading] = useState(false);
  const [sslStatus, setSslStatus] = useState(null);
  const [sslForm] = Form.useForm();
  const sslPollingRef = useRef(null);

  // Certificate library state
  const [certLibrary, setCertLibrary] = useState([]);
  const [certLibraryLoading, setCertLibraryLoading] = useState(false);
  const [applyingCertId, setApplyingCertId] = useState(null);

  // Origin CA state
  const [originCerts, setOriginCerts] = useState([]);
  const [originCertsLoading, setOriginCertsLoading] = useState(false);
  const [originCertModalVisible, setOriginCertModalVisible] = useState(false);
  const [originCertCreateLoading, setOriginCertCreateLoading] = useState(false);
  const [originCertForm] = Form.useForm();
  const [viewCertModal, setViewCertModal] = useState(null);

  useEffect(() => {
    if (domainData) {
      setForceHttps(!!domainData.force_https);
      setAcmeCa(domainData.acme_ca || 'letsencrypt');
      setSslMode(domainData.ssl_mode || 'full');
      headersForm.setFieldsValue(headersToForm(domainData?.settings?.security?.headers));
    }
  }, [domainData]);

  // Load origin certs on mount
  useEffect(() => {
    loadOriginCerts();
  }, []);

  // 列表里有签发中的主机名:弹窗没开时每 15 秒刷新一次(弹窗开着由上面的轮询负责)
  const hostsInProgress = (domainData?.hostnames || []).some((h) => ACME_IN_PROGRESS.includes(h.acme_status));
  useEffect(() => {
    if (!hostsInProgress || sslModalVisible) return undefined;
    const timer = setInterval(() => reloadDomain(), 15000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostsInProgress, sslModalVisible]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (sslPollingRef.current) {
        clearInterval(sslPollingRef.current);
      }
    };
  }, []);

  // ==========================================
  // SSL Mode
  // ==========================================

  const handleSslModeSelect = (mode) => {
    setSslMode(mode);
  };

  const doSslModeSave = async () => {
    setSslModeLoading(true);
    try {
      const response = await cdnService.updateDomain({
        id: domainData.id,
        ssl_mode: sslMode
      });
      if (response.success) {
        message.success(t('Encryption mode updated'));
        reloadDomain();
      } else {
        message.error(response.error || t('Update failed'));
      }
    } catch (error) {
      message.error(t('Update failed'));
    } finally {
      setSslModeLoading(false);
    }
  };

  const handleSslModeSave = async () => {
    if (sslMode === (domainData?.ssl_mode || 'full')) return;
    // 切到 off / flexible 属于安全降级：CDN 到源站将使用明文 HTTP，弹窗警示
    if (sslMode === 'off' || sslMode === 'flexible') {
      const modeLabel = t(SSL_MODES.find(m => m.value === sslMode)?.label || sslMode);
      Modal.confirm({
        title: t('Reduce encryption strength?'),
        okText: t('Switch anyway'),
        cancelText: t('Cancel'),
        okButtonProps: { danger: true },
        content: (
          <div>
            <p>{t('You are changing the encryption mode to ')}<strong>{modeLabel}</strong>。</p>
            <p style={{ color: '#cf1322', marginBottom: 0 }}>
              {sslMode === 'off'
                ? t('In this mode, client connections to CDN are also unencrypted. Data is transmitted in plain text and is vulnerable to interception or tampering.')
                : t('In this mode, CDN connects to the origin over unencrypted HTTP. Traffic between the origin and CDN is vulnerable to interception or tampering.')}
            </p>
          </div>
        ),
        onOk: () => doSslModeSave()
      });
      return;
    }
    doSslModeSave();
  };

  const sslModeChanged = sslMode !== (domainData?.ssl_mode || 'full');

  // ==========================================
  // Security response headers (HSTS / 安全头 / CORS)
  // ==========================================

  const handleSaveHeaders = async () => {
    setHeadersSaving(true);
    try {
      const values = await headersForm.validateFields();
      const headers = buildHeadersPayload(values);
      const response = await cdnService.updateDomain({ id: domainData.id, security: { headers } });
      if (response.success) {
        message.success(t('Security response headers saved'));
        reloadDomain();
      } else {
        message.error(response.error || t('Failed to save'));
      }
    } catch (error) {
      if (error && error.errorFields) return;
      message.error(t('Failed to save'));
    } finally {
      setHeadersSaving(false);
    }
  };

  // ==========================================
  // Force HTTPS
  // ==========================================

  const handleForceHttpsToggle = async (checked) => {
    try {
      const response = await cdnService.updateDomain({
        id: domainData.id,
        force_https: !!checked
      });
      if (response.success) {
        setForceHttps(checked);
        message.success(checked ? t('Force HTTPS enabled') : t('Force HTTPS disabled'));
        reloadDomain();
      } else {
        message.error(response.error || t('Operation failed'));
      }
    } catch (error) {
      message.error(t('Operation failed'));
    }
  };

  const handleAcmeCaChange = async (value) => {
    const prev = acmeCa;
    setAcmeCa(value);
    setAcmeCaSaving(true);
    try {
      const response = await cdnService.updateDomain({ id: domainData.id, acme_ca: value });
      if (response.success) {
        message.success(t('Saved'));
        reloadDomain();
      } else {
        setAcmeCa(prev);
        message.error(response.error || t('Failed to save'));
      }
    } catch (error) {
      setAcmeCa(prev);
      message.error(t('Failed to save'));
    } finally {
      setAcmeCaSaving(false);
    }
  };

  // ==========================================
  // Origin CA Certificates
  // ==========================================

  const loadOriginCerts = async () => {
    setOriginCertsLoading(true);
    try {
      const response = await cdnService.getOriginCerts(1, 50, domainData?.id);
      if (response.success) {
        setOriginCerts(response.data?.items || []);
      }
    } catch (error) {
      console.error('Failed to load origin certs:', error);
    } finally {
      setOriginCertsLoading(false);
    }
  };

  const handleCreateOriginCert = async (values) => {
    setOriginCertCreateLoading(true);
    try {
      const response = await cdnService.createOriginCert({
        hostnames: values.hostnames,
        key_type: values.key_type || 'ecc256',
        validity_days: values.validity_days || 5475,
        domain_id: domainData?.id
      });
      if (response.success) {
        message.success(t('Origin CA certificate issued'));
        setOriginCertModalVisible(false);
        originCertForm.resetFields();
        // Show the generated cert for download
        setViewCertModal(response.data);
        loadOriginCerts();
      } else {
        message.error(response.error || t('Issuance failed'));
      }
    } catch (error) {
      message.error(t('Issuance failed'));
    } finally {
      setOriginCertCreateLoading(false);
    }
  };

  const handleRevokeOriginCert = async (id) => {
    try {
      const response = await cdnService.revokeOriginCert(id);
      if (response.success) {
        message.success(t('Certificate revoked'));
        loadOriginCerts();
      } else {
        message.error(response.error || t('Revocation failed'));
      }
    } catch (error) {
      message.error(t('Revocation failed'));
    }
  };

  const handleDeleteOriginCert = async (id) => {
    try {
      const response = await cdnService.deleteOriginCert(id);
      if (response.success) {
        message.success(t('Certificate deleted'));
        loadOriginCerts();
      } else {
        message.error(response.error || t('Deletion failed'));
      }
    } catch (error) {
      message.error(t('Deletion failed'));
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success(t('{{label}} copied to clipboard', { label }));
    }).catch(() => {
      message.error(t('Failed to copy'));
    });
  };

  // 下载平台 Origin CA 根证书（安装到源站信任链，用于「完全（严格）」模式验证）。
  const handleDownloadCaCert = async () => {
    try {
      const response = await cdnService.getOriginCaCert();
      const pem = response?.data?.certificate;
      if (!response.success || !pem) {
        message.error(response.error || t('Failed to retrieve CA root certificate'));
        return;
      }
      const blob = new Blob([pem], { type: 'application/x-pem-file' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'origin_ca_root.pem';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      message.success(t('CA root certificate downloaded'));
    } catch (error) {
      message.error(t('Download failed'));
    }
  };

  const openCreateOriginCertModal = () => {
    const domain = domainData?.domain || '';
    originCertForm.setFieldsValue({
      hostnames: domain ? [domain, `*.${domain}`] : [],
      key_type: 'ecc256',
      validity_days: 5475
    });
    setOriginCertModalVisible(true);
  };

  // ==========================================
  // Per-hostname SSL (existing logic)
  // ==========================================

  // 从源站设置等页面跳转过来:?hostname=<id> 自动打开该主机名的证书弹窗
  const [searchParams, setSearchParams] = useSearchParams();
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    const want = searchParams.get('hostname');
    if (!want || autoOpenedRef.current || !domainData) return;
    const h = (domainData.hostnames || []).find((x) => String(x.id) === want || x.hostname === want);
    if (!h) return;
    autoOpenedRef.current = true;
    const next = new URLSearchParams(searchParams);
    next.delete('hostname');
    setSearchParams(next, { replace: true });
    openSslModal(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainData, searchParams]);

  const openSslModal = async (hostname) => {
    setSslHostname(hostname);
    setSslModalVisible(true);
    setSslLoading(true);
    try {
      const [statusResponse] = await Promise.all([
        cdnService.getHostnameAcmeStatus(hostname.id),
        loadCertLibrary()
      ]);
      if (statusResponse.success) {
        setSslStatus(statusResponse.data);
        if (ACME_IN_PROGRESS.includes(statusResponse.data?.acme_status)) {
          startSslStatusPolling(hostname.id);
        }
      }
    } catch (error) {
      console.error('Failed to get SSL status:', error);
    } finally {
      setSslLoading(false);
    }
  };

  const closeSslModal = () => {
    setSslModalVisible(false);
    setSslHostname(null);
    setSslStatus(null);
    if (sslPollingRef.current) {
      clearInterval(sslPollingRef.current);
      sslPollingRef.current = null;
    }
  };

  const handleEnableSsl = async (enabled, sslType = 'auto') => {
    if (!sslHostname) return;
    setSslLoading(true);
    try {
      const response = await cdnService.enableHostnameSsl(sslHostname.id, enabled, sslType);
      if (response.success) {
        message.success(enabled ? t('SSL enabled') : t('SSL disabled'));
        reloadDomain();
        const statusResponse = await cdnService.getHostnameAcmeStatus(sslHostname.id);
        if (statusResponse.success) {
          setSslStatus(statusResponse.data);
        }
      } else {
        message.error(response.error || t('Operation failed'));
      }
    } catch (error) {
      message.error(t('Operation failed'));
    } finally {
      setSslLoading(false);
    }
  };

  // 不指定验证方式:后端按域名选(泛域名 dns-01,其余 http-01)
  const handleRequestAcme = async (validationType) => {
    if (!sslHostname) return;
    setSslLoading(true);
    try {
      const response = await cdnService.requestHostnameAcme(sslHostname.id, validationType);
      if (response.success) {
        message.success(t('ACME certificate request submitted'));
        reloadDomain();
        startSslStatusPolling(sslHostname.id);
      } else {
        message.error(response.error || t('Request failed'));
      }
    } catch (error) {
      message.error(t('Request failed'));
    } finally {
      setSslLoading(false);
    }
  };

  // 传 id 而不是读 sslHostname:刚 setSslHostname 时闭包里还是旧值
  const startSslStatusPolling = (hostnameId) => {
    if (sslPollingRef.current) {
      clearInterval(sslPollingRef.current);
    }
    if (!hostnameId) return;
    sslPollingRef.current = setInterval(async () => {
      try {
        const response = await cdnService.getHostnameAcmeStatus(hostnameId);
        if (response.success) {
          setSslStatus(response.data);
          if (!ACME_IN_PROGRESS.includes(response.data.acme_status)) {
            clearInterval(sslPollingRef.current);
            sslPollingRef.current = null;
            reloadDomain();
            if (response.data.acme_status === 'issued') {
              message.success(t('SSL certificate issued'));
            }
          }
        }
      } catch (error) {
        console.error('Failed to poll SSL status:', error);
      }
    }, 5000);
  };

  // 已上传过自定义证书:回显当前证书内容供直接修改;私钥不回显,留空即沿用
  const uploadedCert = sslStatus?.certificate?.cert_type === 'uploaded' ? sslStatus.certificate : null;
  useEffect(() => {
    if (uploadedCert) {
      sslForm.setFieldsValue({ certificate: uploadedCert.certificate_pem || '', private_key: '' });
    }
  }, [uploadedCert?.id, uploadedCert?.certificate_pem]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUploadCert = async (values) => {
    if (!sslHostname) return;
    setSslLoading(true);
    try {
      const response = await cdnService.uploadHostnameSsl(
        sslHostname.id,
        values.certificate,
        values.private_key
      );
      if (response.success) {
        message.success(t('Certificate uploaded'));
        reloadDomain();
        const statusResponse = await cdnService.getHostnameAcmeStatus(sslHostname.id);
        if (statusResponse.success) {
          setSslStatus(statusResponse.data);
        }
      } else {
        message.error(response.error || t('Upload failed'));
      }
    } catch (error) {
      message.error(t('Upload failed'));
    } finally {
      setSslLoading(false);
    }
  };

  const loadCertLibrary = async () => {
    setCertLibraryLoading(true);
    try {
      const response = await cdnService.getCertLibrary();
      if (response.success) {
        setCertLibrary(response.data || []);
      }
    } catch (error) {
      console.error('Failed to load cert library:', error);
    } finally {
      setCertLibraryLoading(false);
    }
  };

  const handleApplyCert = async (certId) => {
    if (!sslHostname) return;
    setApplyingCertId(certId);
    try {
      const response = await cdnService.applyCertToHostname(certId, sslHostname.id);
      if (response.success) {
        message.success(t('Certificate deployed'));
        reloadDomain();
        const statusResponse = await cdnService.getHostnameAcmeStatus(sslHostname.id);
        if (statusResponse.success) {
          setSslStatus(statusResponse.data);
        }
      } else {
        message.error(response.error || t('Deployment failed'));
      }
    } catch (error) {
      message.error(t('Deployment failed'));
    } finally {
      setApplyingCertId(null);
    }
  };

  const getCompatibleCerts = () => {
    if (!sslHostname) return [];
    return certLibrary.filter(cert => {
      const domains = [cert.domain, ...ensureArray(cert.san_domains)];
      return domains.some(d => {
        if (d === sslHostname.hostname) return true;
        if (d.startsWith('*.')) {
          const base = d.substring(2);
          const parts = sslHostname.hostname.split('.');
          if (parts.length >= 2) {
            return parts.slice(1).join('.') === base;
          }
        }
        return false;
      });
    });
  };

  const getSslStatusTag = (hostname) => {
    const status = hostname.ssl_status;
    if (!status || !status.enabled) {
      return <Tag>{t('Disabled')}</Tag>;
    }
    const acmeStatus = status.acme_status;
    if (status.type === 'custom') {
      return <Tag color="green" icon={<CheckCircleOutlined />}>{t('Custom certificate')}</Tag>;
    }
    switch (acmeStatus) {
      case 'pending':
        return <Tag color="processing" icon={<LoadingOutlined />}>{t('Waiting')}</Tag>;
      case 'verifying':
        return <Tag color="processing" icon={<SyncOutlined spin />}>{t('Verifying')}</Tag>;
      case 'issuing':
        return <Tag color="processing" icon={<SyncOutlined spin />}>{t('Issuing')}</Tag>;
      case 'issued':
        return <Tag color="success" icon={<CheckCircleOutlined />}>{t('Issued')}</Tag>;
      case 'failed':
        return <Tag color="error" icon={<CloseCircleOutlined />}>{t('Failed')}</Tag>;
      default:
        return <Tag color="blue" icon={<LockOutlined />}>{t('Enabled')}</Tag>;
    }
  };

  const hostnames = domainData?.hostnames || [];

  // Desktop columns for hostname SSL table
  const columns = [
    {
      title: t('Hostname'),
      dataIndex: 'hostname',
      key: 'hostname',
      render: (text) => <Text strong style={{ wordBreak: 'break-all' }}>{text}</Text>
    },
    {
      title: t('SSL status'),
      key: 'ssl_status',
      width: 120,
      render: (_, record) => getSslStatusTag(record)
    },
    {
      title: t('Issuer'),
      key: 'ssl_issuer',
      width: 140,
      render: (_, record) => {
        const status = record.ssl_status;
        if (!status || !status.enabled) return <Text type="secondary">-</Text>;
        const issuer = status.certificate?.issuer;
        if (issuer) return <Text style={{ fontSize: 12 }}>{issuer}</Text>;
        if (status.type === 'custom') return <Tag>{t('Custom')}</Tag>;
        return <Tag color="blue">ACME</Tag>;
      }
    },
    {
      title: t('Expires at'),
      key: 'ssl_expires',
      width: 160,
      render: (_, record) => {
        const status = record.ssl_status;
        const expiresAt = status?.certificate?.expires_at || status?.expires_at || status?.expire_at;
        if (!expiresAt) return <Text type="secondary">-</Text>;
        const dateStr = typeof expiresAt === 'string' ? expiresAt.replace('T', ' ').replace(/\.\d+Z$/, '').split(' ')[0] : expiresAt;
        return <Text style={{ fontSize: 12 }}>{dateStr}</Text>;
      }
    },
    {
      title: t('Actions'),
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<LockOutlined />}
          onClick={() => openSslModal(record)}
        >{t('Manage certificates')}</Button>
      )
    }
  ];

  // Origin cert columns
  const originCertColumns = [
    {
      title: t('Domains'),
      key: 'hostnames',
      render: (_, record) => (
        <Space size={4} wrap>
          {ensureArray(record.hostnames).map(h => (
            <Tag key={h} style={{ fontSize: 12 }}>{h}</Tag>
          ))}
        </Space>
      )
    },
    {
      title: t('Key type'),
      dataIndex: 'key_type',
      key: 'key_type',
      width: 90,
      render: (v) => <Tag>{(v || '').toUpperCase()}</Tag>
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (v) => v === 'active'
        ? <Tag color="green">{t('Valid')}</Tag>
        : <Tag color="red">{t('Revoked')}</Tag>
    },
    {
      title: t('Expires at'),
      dataIndex: 'expires_at',
      key: 'expires_at',
      width: 120,
      render: (v) => <Text style={{ fontSize: 12 }}>{(v || '').split(' ')[0]}</Text>
    },
    {
      title: t('Actions'),
      key: 'action',
      width: 160,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title={t('View certificate')}>
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={async () => {
                const resp = await cdnService.getOriginCert(record.id);
                if (resp.success) {
                  setViewCertModal(resp.data);
                }
              }}
            />
          </Tooltip>
          {record.status === 'active' && (
            <Popconfirm
              title={t('Revoke this certificate?')}
              onConfirm={() => handleRevokeOriginCert(record.id)}
              okText={t('Revoke')}
              cancelText={t('Cancel')}
            >
              <Button type="link" size="small" danger icon={<StopOutlined />} />
            </Popconfirm>
          )}
          <Popconfirm
            title={t('Delete this certificate? This action cannot be undone.')}
            onConfirm={() => handleDeleteOriginCert(record.id)}
            okText={t('Delete')}
            cancelText={t('Cancel')}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Mobile card for each hostname
  const renderMobileCard = (item) => (
    <Card
      key={item.id}
      size="small"
      style={{ marginBottom: 12 }}
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text strong style={{ fontSize: 13, wordBreak: 'break-all', flex: 1, minWidth: 0 }}>
            {item.hostname}
          </Text>
          {getSslStatusTag(item)}
        </div>
      }
    >
      <div style={{ fontSize: 12, marginBottom: 8 }}>
        {item.ssl_status?.enabled ? (
          <Space wrap size="small">
            <Tag>{item.ssl_status.type === 'custom' ? t('Custom certificate') : 'ACME'}</Tag>
            {(item.ssl_status.certificate?.expires_at || item.ssl_status.expires_at) && (
              <Text type="secondary">{t('Expires: {{value}}', { value: (item.ssl_status.certificate?.expires_at || item.ssl_status.expires_at || '').replace('T', ' ').replace(/\.\d+Z$/, '').split(' ')[0] })}</Text>
            )}
          </Space>
        ) : (
          <Text type="secondary">{t('No SSL certificate configured')}</Text>
        )}
      </div>
      <Button
        size="small"
        icon={<LockOutlined />}
        onClick={() => openSslModal(item)}
      >{t('Manage certificates')}</Button>
    </Card>
  );

  // Mobile card for origin certs
  const renderOriginCertMobileCard = (cert) => (
    <Card key={cert.id} size="small" style={{ marginBottom: 12 }}>
      <div style={{ marginBottom: 8 }}>
        <Space size={4} wrap>
          {ensureArray(cert.hostnames).map(h => (
            <Tag key={h} style={{ fontSize: 11 }}>{h}</Tag>
          ))}
        </Space>
      </div>
      <div style={{ fontSize: 12, marginBottom: 8 }}>
        <Space wrap size="small">
          <Tag>{(cert.key_type || '').toUpperCase()}</Tag>
          {cert.status === 'active'
            ? <Tag color="green">{t('Valid')}</Tag>
            : <Tag color="red">{t('Revoked')}</Tag>
          }
          <Text type="secondary">{t('Expires: {{value}}', { value: (cert.expires_at || '').split(' ')[0] })}</Text>
        </Space>
      </div>
      <Space size={8}>
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={async () => {
            const resp = await cdnService.getOriginCert(cert.id);
            if (resp.success) {
              setViewCertModal(resp.data);
            }
          }}
        >{t('View')}</Button>
        {cert.status === 'active' && (
          <Popconfirm
            title={t('Revoke this certificate?')}
            onConfirm={() => handleRevokeOriginCert(cert.id)}
            okText={t('Revoke')}
            cancelText={t('Cancel')}
          >
            <Button size="small" danger icon={<StopOutlined />}>{t('Revoke')}</Button>
          </Popconfirm>
        )}
        <Popconfirm
          title={t('Delete this certificate?')}
          onConfirm={() => handleDeleteOriginCert(cert.id)}
          okText={t('Delete')}
          cancelText={t('Cancel')}
        >
          <Button size="small" danger icon={<DeleteOutlined />}>{t('Delete')}</Button>
        </Popconfirm>
      </Space>
    </Card>
  );

  return (
    <div style={{ padding: isMobile ? '12px 0' : '24px 0' }}>
      {/* 加密模式选择 */}
      <Card
        title={
          <Space>
            <CloudServerOutlined />
            <span>{t('SSL/TLS encryption mode')}</span>
          </Space>
        }
        size={isMobile ? 'small' : 'default'}
        style={{ marginBottom: isMobile ? 12 : 24 }}
      >
        <Spin spinning={sslModeLoading}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
            gap: 8,
            marginBottom: 12
          }}>
            {SSL_MODES.map(mode => {
              const selected = sslMode === mode.value;
              return (
                <div
                  key={mode.value}
                  onClick={() => handleSslModeSelect(mode.value)}
                  style={{
                    padding: '10px 12px',
                    border: `2px solid ${selected ? mode.color : '#d9d9d9'}`,
                    borderRadius: 8,
                    background: selected ? `${mode.color}0a` : '#fafafa',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: mode.color, margin: '0 auto 6px'
                  }} />
                  <Text strong style={{ fontSize: 13, color: selected ? mode.color : undefined }}>
                    {t(mode.label)}
                  </Text>
                </div>
              );
            })}
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '8px 12px',
            background: '#f6f6f6',
            borderRadius: 6,
            fontSize: 13,
            color: '#555'
          }}>
            <span>{t(SSL_MODES.find(m => m.value === sslMode)?.description)}</span>
            {sslModeChanged && (
              <Button
                type="primary"
                size="small"
                onClick={handleSslModeSave}
                loading={sslModeLoading}
              >{t('Save')}</Button>
            )}
          </div>
        </Spin>
      </Card>

      {/* 强制 HTTPS */}
      <Card
        title={
          <Space>
            <LockOutlined />
            <span>{t('HTTPS settings')}</span>
          </Space>
        }
        size={isMobile ? 'small' : 'default'}
        style={{ marginBottom: isMobile ? 12 : 24 }}
      >
        <div style={{ marginBottom: 16 }}>
          <Space size="large" wrap>
            <span><strong>{t('Force HTTPS:')}</strong></span>
            <Switch
              checked={forceHttps}
              onChange={handleForceHttpsToggle}
              checkedChildren={t('Enable')}
              unCheckedChildren={t('Disable')}
            />
          </Space>
          <div style={{ marginTop: 8, color: '#666', fontSize: isMobile ? 12 : 13 }}>{t('Require clients to use HTTPS. When enabled, all HTTP requests are automatically redirected to HTTPS with a 301 redirect. This only controls client access and does not affect the origin protocol, which is configured in origin settings.')}</div>
        </div>
        <Space size="large" wrap>
          <span>
            <strong>{t('Certificate authority')}</strong>{' '}
            <Tooltip title={t('Used for the next issue or renewal; falls back to the other CA automatically if issuance fails.')}>
              <QuestionCircleOutlined style={{ color: '#999' }} />
            </Tooltip>
          </span>
          <Select
            value={acmeCa}
            onChange={handleAcmeCaChange}
            loading={acmeCaSaving}
            disabled={acmeCaSaving}
            options={ACME_CAS}
            style={{ width: 160 }}
          />
        </Space>
      </Card>

      {/* HSTS + 安全响应头 → settings.security.headers */}
      <Card
        title={<Space><SafetyCertificateOutlined /><span>{t('HSTS & security response headers')}</span></Space>}
        size={isMobile ? 'small' : 'default'}
        style={{ marginBottom: isMobile ? 12 : 24 }}
        extra={<Button type="primary" onClick={handleSaveHeaders} loading={headersSaving} size={isMobile ? 'small' : 'middle'}>{t('Save')}</Button>}
      >
        <Alert
          message={t('These response headers are added at the edge to improve browser security. Enable HSTS with care: once the browser caches it, HTTPS is enforced for the max-age duration.')}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form form={headersForm} layout="vertical" size={isMobile ? 'small' : 'middle'}>
          <Divider orientation="left" style={{ margin: '4px 0 12px' }}>{t('HSTS (HTTP Strict Transport Security)')}<Form.Item name={['hsts', 'enabled']} valuePropName="checked" noStyle>
              <Switch size="small" checkedChildren={t('Enable')} unCheckedChildren={t('Disable')} style={{ marginLeft: 12 }} />
            </Form.Item>
          </Divider>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name={['hsts', 'max_age']} label={t('Duration (max-age)')} tooltip={t('How long browsers force HTTPS. Start short, then use 1 year.')}>
                <DurationInput presets={[300, 86400, 604800, 2592000, 15552000, 31536000, 63072000]} min={0} max={63072000} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item name={['hsts', 'include_sub_domains']} label={t('Include subdomains')} valuePropName="checked" tooltip={t('Also enforce HTTPS on all subdomains')}><Switch /></Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item name={['hsts', 'preload']} label="Preload" valuePropName="checked" tooltip={t('Allow inclusion in the browser HSTS preload list (preload requirements must be met)')}><Switch /></Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ margin: '4px 0 12px' }}>{t('Security response headers')}<Form.Item name={['security', 'enabled']} valuePropName="checked" noStyle>
              <Switch size="small" checkedChildren={t('Enable')} unCheckedChildren={t('Disable')} style={{ marginLeft: 12 }} />
            </Form.Item>
          </Divider>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name={['security', 'frame_options']} label="X-Frame-Options" tooltip={t('Clickjacking protection: control whether the page can be embedded in an iframe')}>
                <Select options={FRAME_OPTIONS.map(option => ({ ...option, label: t(option.label) }))} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name={['security', 'referrer_policy']} label="Referrer-Policy">
                <Select options={REFERRER_POLICIES.map(option => ({ ...option, label: t(option.label) }))} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name={['security', 'content_type_options']} label="X-Content-Type-Options: nosniff" valuePropName="checked" tooltip={t('Disable browser MIME sniffing')}><Switch /></Form.Item>
            </Col>
          </Row>
          <Form.Item name={['security', 'csp_report_only']} valuePropName="checked" hidden><Switch /></Form.Item>
          <Form.Item noStyle shouldUpdate={(a, b) => a.security?.csp_report_only !== b.security?.csp_report_only}>
            {({ getFieldValue, setFieldValue }) => (
              <Form.Item name={['security', 'csp']} label="Content-Security-Policy (CSP)" tooltip={t('Leave empty to not send the header')}>
                <CspBuilder
                  reportOnly={!!getFieldValue(['security', 'csp_report_only'])}
                  onReportOnlyChange={(c) => setFieldValue(['security', 'csp_report_only'], c)}
                />
              </Form.Item>
            )}
          </Form.Item>

          <Collapse
            ghost
            style={{ marginTop: 4 }}
            items={[{
              key: 'cors',
              // forceRender：CORS 子字段常驻注册，避免面板未展开时 validateFields 收不到已存值 → 保存被清空。
              forceRender: true,
              label: (
                <Space>
                  <span>{t('CORS (Cross-Origin Resource Sharing)')}</span>
                  <Form.Item name={['cors', 'enabled']} valuePropName="checked" noStyle>
                    <Switch size="small" checkedChildren={t('Enable')} unCheckedChildren={t('Disable')} onClick={(c, e) => e && e.stopPropagation && e.stopPropagation()} />
                  </Form.Item>
                </Space>
              ),
              children: (
                <>
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item name={['cors', 'allow_origins']} label={t('Allowed origins (Access-Control-Allow-Origin)')} tooltip={t('* allows any origin; https://*.example.com allows subdomains')}><TagsInput format="lines" placeholder="https://example.com  *" validate={(o) => (isOrigin(o) ? null : t('Use * or scheme://host[:port], e.g. https://example.com'))} /></Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item name={['cors', 'allow_methods']} label={t('Allowed methods')}>
                        <Select mode="multiple" options={CORS_METHODS.map((m) => ({ value: m, label: m }))} placeholder="GET POST ..." />
                      </Form.Item>
                      <Form.Item name={['cors', 'max_age']} label={t('Preflight cache (max-age)')} tooltip={t('Browsers cap this: Chrome at 2 hours, Firefox at 1 day')}><DurationInput presets={[600, 3600, 7200, 86400]} zeroLabel={t('Not sent')} min={0} max={86400} /></Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item name={['cors', 'allow_headers']} label={t('Allowed request headers')} ><TagsInput format="lines" placeholder="Content-Type, Authorization" validate={(h) => (h === '*' || isHeaderName(h) ? null : t('Invalid header name'))} /></Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item name={['cors', 'expose_headers']} label={t('Exposed response headers')}><TagsInput format="lines" validate={(h) => (h === '*' || isHeaderName(h) ? null : t('Invalid header name'))} /></Form.Item>
                    </Col>
                  </Row>
                  <Form.Item name={['cors', 'allow_credentials']} label={t('Allow credentials (Allow-Credentials)')} valuePropName="checked" tooltip={t('Cannot be used with origin * when enabled')}><Switch /></Form.Item>
                </>
              ),
            }]}
          />

          <Divider orientation="left" style={{ margin: '12px 0' }}>{t('Custom response headers')}</Divider>
          <Alert
            message={t('Directly control the response headers the edge sends to clients. set overwrites headers with the same name, add appends headers (duplicates allowed), and remove deletes specified headers.')}
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
          />

          <div style={{ marginBottom: 4, fontWeight: 500 }}>{t('Set / overwrite (set)')}</div>
          <Form.List name="set">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Row gutter={8} key={key} style={{ marginBottom: 8 }} align="middle">
                    <Col xs={10} sm={8}>
                      <Form.Item {...restField} name={[name, 'name']} style={{ marginBottom: 0 }} rules={[{ required: true, message: t('Header name') }]}>
                        <Input placeholder={t('Header name, e.g. X-Frame-Options')} />
                      </Form.Item>
                    </Col>
                    <Col xs={11} sm={14}>
                      <Form.Item {...restField} name={[name, 'value']} style={{ marginBottom: 0 }}>
                        <Input placeholder={t('Header value')} />
                      </Form.Item>
                    </Col>
                    <Col xs={3} sm={2}>
                      <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)} />
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" onClick={() => add({ name: '', value: '' })} icon={<PlusOutlined />} block style={{ marginBottom: 16 }}>{t('Add set header')}</Button>
              </>
            )}
          </Form.List>

          <div style={{ marginBottom: 4, fontWeight: 500 }}>{t('Append (add)')}</div>
          <Form.List name="add">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Row gutter={8} key={key} style={{ marginBottom: 8 }} align="middle">
                    <Col xs={10} sm={8}>
                      <Form.Item {...restField} name={[name, 'name']} style={{ marginBottom: 0 }} rules={[{ required: true, message: t('Header name') }]}>
                        <Input placeholder={t('Header name, e.g. Set-Cookie')} />
                      </Form.Item>
                    </Col>
                    <Col xs={11} sm={14}>
                      <Form.Item {...restField} name={[name, 'value']} style={{ marginBottom: 0 }}>
                        <Input placeholder={t('Header value')} />
                      </Form.Item>
                    </Col>
                    <Col xs={3} sm={2}>
                      <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)} />
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" onClick={() => add({ name: '', value: '' })} icon={<PlusOutlined />} block style={{ marginBottom: 16 }}>{t('Add add header')}</Button>
              </>
            )}
          </Form.List>

          <Form.Item name="remove" label={t('Remove response header (remove)')} tooltip={t('The edge removes these headers from responses (one per line)')}>
            <Select mode="tags" placeholder={t('Header name to remove, such as Server or X-Powered-By')} style={{ width: '100%' }} />
          </Form.Item>

        </Form>
      </Card>

      {/* Per-hostname SSL 管理 */}
      <Card
        title={
          <Space>
            <SafetyCertificateOutlined />
            <span>{t('Edge certificate management')}</span>
          </Space>
        }
        size={isMobile ? 'small' : 'default'}
        style={{ marginBottom: isMobile ? 12 : 24 }}
      >
        <Alert
          message={t('Each hostname (subdomain) can have its own SSL certificate')}
          description={t('Click "Manage certificates" to request a free certificate or upload a custom certificate for the hostname. These certificates encrypt HTTPS connections between clients (browsers) and CDN edge nodes.')}
          type="info"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />

        {hostnames.length === 0 ? (
          <Alert
            message={t('No origin configuration yet')}
            description={t('Please add a hostname in Origin settings before configuring an SSL certificate.')}
            type="info"
            showIcon
          />
        ) : isMobile ? (
          <div>
            {hostnames.map(item => renderMobileCard(item))}
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={hostnames}
            rowKey="id"
            pagination={false}
            size="small"
          />
        )}
      </Card>

      {/* Origin CA 证书管理 */}
      <Card
        title={
          <Space>
            <SafetyCertificateOutlined />
            <span>{t('Origin CA certificate')}</span>
          </Space>
        }
        size={isMobile ? 'small' : 'default'}
        extra={
          <Space size={isMobile ? 4 : 8}>
            <Button
              icon={<DownloadOutlined />}
              size={isMobile ? 'small' : 'middle'}
              onClick={handleDownloadCaCert}
            >
              {isMobile ? 'CA' : t('Download CA root certificate')}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size={isMobile ? 'small' : 'middle'}
              onClick={openCreateOriginCertModal}
            >
              {isMobile ? t('Issue') : t('Issue certificate')}
            </Button>
          </Space>
        }
      >
        <Alert
          message={t('Origin CA certificates encrypt traffic between the origin and the CDN')}
          description={
            <span>{t('Install a platform-issued Origin CA certificate on your origin server and use Full (Strict) encryption mode. Origin CA certificates are trusted only by this platform\'s CDN, not by browsers, so they are used only to encrypt connections between CDN and origin. Certificates are valid for up to 15 years.')}</span>
          }
          type="info"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />

        {originCertsLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin />
          </div>
        ) : originCerts.length === 0 ? (
          <Alert
            message={t('No Origin CA certificates yet')}
            description={t('Click "Issue certificate" to generate an Origin CA certificate for your origin.')}
            type="info"
            showIcon
          />
        ) : isMobile ? (
          <div>
            {originCerts.map(cert => renderOriginCertMobileCard(cert))}
          </div>
        ) : (
          <Table
            columns={originCertColumns}
            dataSource={originCerts}
            rowKey="id"
            pagination={false}
            size="small"
          />
        )}
      </Card>

      {/* Per-hostname SSL Modal */}
      <Modal
        title={
          <Space>
            <SafetyCertificateOutlined />
            <span style={{ fontSize: isMobile ? 14 : 16 }}>{t('SSL certificate configuration ')}{sslHostname && `- ${sslHostname.hostname}`}
            </span>
          </Space>
        }
        open={sslModalVisible}
        onCancel={closeSslModal}
        footer={null}
        width={isMobile ? '100%' : 700}
        style={isMobile ? { top: 20 } : undefined}
        styles={isMobile ? { body: { maxHeight: '70vh', overflow: 'auto' } } : undefined}
      >
        {sslLoading && !sslStatus ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>{t('Loading...')}</div>
          </div>
        ) : (
          <div>
            {/* Current Status */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }} size={4}>
                <div>
                  <Text strong>{t('Status: ')}</Text>
                  {sslHostname && getSslStatusTag(sslHostname)}
                </div>
                {sslStatus?.certificate && (
                  <>
                    {(sslStatus.certificate.issuer || caLabel(sslStatus.certificate.issuer_ca)) && (
                      <div>
                        <Text strong>{t('Issuer: ')}</Text>
                        <Text>{sslStatus.certificate.issuer || caLabel(sslStatus.certificate.issuer_ca)}</Text>
                      </div>
                    )}
                    {sslStatus.certificate.common_name && (
                      <div>
                        <Text strong>CN: </Text>
                        <Text code>{sslStatus.certificate.common_name}</Text>
                      </div>
                    )}
                    {sslStatus.certificate.san && (
                      <div>
                        <Text strong>SANs: </Text>
                        <span>
                          {ensureArray(
                            typeof sslStatus.certificate.san === 'string'
                              ? sslStatus.certificate.san.split(',').map(s => s.trim())
                              : sslStatus.certificate.san
                          ).map(s => <Tag key={s} style={{ fontSize: 11, marginBottom: 2 }}>{s}</Tag>)}
                        </span>
                      </div>
                    )}
                    {sslStatus.certificate.cert_type && (
                      <div>
                        <Text strong>{t('Type: ')}</Text>
                        <Tag color="blue">{
                          { letsencrypt: "Let's Encrypt", uploaded: t('Custom upload'), 'self-signed': t('Self-signed'), system: t('System') }[sslStatus.certificate.cert_type] || sslStatus.certificate.cert_type
                        }</Tag>
                      </div>
                    )}
                    <div>
                      <Text strong>{t('Validity: ')}</Text>
                      <Text>
                        {(sslStatus.certificate.valid_from || '').replace('T', ' ').replace(/\.\d+Z$/, '').split(' ')[0] || '-'}
                        {' ~ '}
                        {(sslStatus.certificate.expires_at || '').replace('T', ' ').replace(/\.\d+Z$/, '').split(' ')[0] || '-'}
                      </Text>
                    </div>
                  </>
                )}
                {sslStatus?.acme_error && (
                  <Alert
                    type="error"
                    message={t('Certificate request failed')}
                    description={sslStatus.acme_error}
                    showIcon
                    style={{ marginTop: 8 }}
                  />
                )}
              </Space>
            </Card>

            {/* ACME Progress */}
            {sslStatus?.acme_status && ['pending', 'verifying', 'issuing'].includes(sslStatus.acme_status) && (
              <Card size="small" title={t('ACME certificate request progress')} style={{ marginBottom: 16 }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Progress
                    percent={
                      sslStatus.acme_status === 'pending' ? 20 :
                      sslStatus.acme_status === 'verifying' ? 50 :
                      sslStatus.acme_status === 'issuing' ? 80 : 0
                    }
                    status="active"
                    size={isMobile ? 'small' : 'default'}
                  />
                  <div>
                    <Tag color="processing" icon={<SyncOutlined spin />}>
                      {sslStatus.acme_status === 'pending' ? t('Pending') :
                       sslStatus.acme_status === 'verifying' ? t('Verifying domain') :
                       sslStatus.acme_status === 'issuing' ? t('Issuing certificate') : t('Processing')}
                    </Tag>
                  </div>
                </Space>
              </Card>
            )}

            <Divider />

            <Tabs
              size={isMobile ? 'small' : 'default'}
              items={[
                {
                  key: 'auto',
                  label: isMobile ? t('Automatic certificate') : t('Automatic certificate (Let\'s Encrypt)'),
                  children: (
                    <div>
                      <Alert
                        message={t('Free automatic certificate')}
                        description={isMobile
                          ? t('Automatically issue certificates with Let\'s Encrypt, completely free.')
                          : t('Automatically issue and renew SSL certificates with Let\'s Encrypt, completely free. Certificates are valid for 90 days and renew automatically.')
                        }
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                      />
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <div>
                          <div>
                            <Button
                              type="primary"
                              icon={<SafetyCertificateOutlined />}
                              loading={sslLoading}
                              onClick={() => handleRequestAcme()}
                              disabled={sslStatus?.acme_status && ['pending', 'verifying', 'issuing'].includes(sslStatus.acme_status)}
                              size={isMobile ? 'small' : 'middle'}
                            >
                              {sslStatus?.certificate ? t('Renew certificate') : t('Request certificate')}
                            </Button>
                            {!String(sslHostname?.hostname || '').startsWith('*.') && (
                              <div style={{ marginTop: 4 }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>{t('The domain must resolve to the CDN')}</Text>
                              </div>
                            )}
                          </div>
                        </div>
                        {sslStatus?.ssl_enabled && (
                          <div style={{ marginTop: 16 }}>
                            <Button
                              danger
                              onClick={() => handleEnableSsl(false)}
                              loading={sslLoading}
                              size={isMobile ? 'small' : 'middle'}
                            >{t('Disable SSL')}</Button>
                          </div>
                        )}
                      </Space>
                    </div>
                  )
                },
                {
                  key: 'custom',
                  label: t('Custom certificate'),
                  children: (
                    <div>
                      <Alert
                        message={t('Upload custom certificate')}
                        description={t('Upload an SSL certificate you purchased yourself.')}
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                      />
                      <Form
                        form={sslForm}
                        layout="vertical"
                        onFinish={handleUploadCert}
                        size={isMobile ? 'middle' : 'default'}
                      >
                        <Form.Item
                          name="certificate"
                          label={t('Certificate (PEM format)')}
                          rules={[{ required: true, message: t('Enter the certificate contents') }]}
                        >
                          <TextArea
                            rows={isMobile ? 4 : 6}
                            placeholder="-----BEGIN CERTIFICATE-----"
                          />
                        </Form.Item>
                        <Form.Item
                          name="private_key"
                          label={t('Private key (PEM format)')}
                          rules={[{ required: !uploadedCert?.has_private_key, message: t('Enter the private key contents') }]}
                        >
                          <TextArea
                            rows={isMobile ? 4 : 6}
                            placeholder={uploadedCert?.has_private_key ? t('Leave blank to keep the current private key') : '-----BEGIN PRIVATE KEY-----'}
                          />
                        </Form.Item>
                        <Form.Item>
                          <Button
                            type="primary"
                            htmlType="submit"
                            icon={<UploadOutlined />}
                            loading={sslLoading}
                            size={isMobile ? 'small' : 'middle'}
                          >{uploadedCert ? t('Update certificate') : t('Upload certificate')}</Button>
                        </Form.Item>
                      </Form>
                    </div>
                  )
                },
                {
                  key: 'library',
                  label: (
                    <Space size={4}>
                      <FolderOpenOutlined />
                      <span>{t('Certificate folder')}</span>
                    </Space>
                  ),
                  children: (
                    <div>
                      <Alert
                        message={t('Select from certificate management')}
                        description={t('Select an existing certificate from SSL certificate management to deploy to this hostname. Wildcard certificates can be deployed to multiple subdomains in one click.')}
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                      />
                      {certLibraryLoading ? (
                        <div style={{ textAlign: 'center', padding: '40px 0' }}>
                          <Spin />
                        </div>
                      ) : (() => {
                        const compatible = getCompatibleCerts();
                        const incompatible = certLibrary.filter(c => !compatible.includes(c));
                        return compatible.length === 0 && incompatible.length === 0 ? (
                          <Alert
                            message={t('No certificates available')}
                            description={t('Please purchase or upload a certificate in SSL certificate management first.')}
                            type="warning"
                            showIcon
                          />
                        ) : (
                          <div>
                            {compatible.length > 0 && (
                              <div style={{ marginBottom: 16 }}>
                                <Text strong style={{ marginBottom: 8, display: 'block' }}>{t('Matching certificates')}</Text>
                                {compatible.map(cert => (
                                  <Card
                                    key={cert.id}
                                    size="small"
                                    style={{ marginBottom: 8 }}
                                    styles={{ body: { padding: '8px 12px' } }}
                                  >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <Text strong style={{ wordBreak: 'break-all' }}>{cert.domain}</Text>
                                        <div style={{ marginTop: 4 }}>
                                          <Space size={4} wrap>
                                            {ensureArray(cert.san_domains).slice(0, 3).map(d => (
                                              <Tag key={d} style={{ fontSize: 11 }}>{d}</Tag>
                                            ))}
                                            {ensureArray(cert.san_domains).length > 3 && (
                                              <Tag style={{ fontSize: 11 }}>+{ensureArray(cert.san_domains).length - 3}</Tag>
                                            )}
                                          </Space>
                                        </div>
                                        <div style={{ marginTop: 4 }}>
                                          <Text type="secondary" style={{ fontSize: 11 }}>{t('{{issuer}} · Expires {{value}}', { issuer: cert.issuer, value: (cert.expires_at || '').split(' ')[0] })}</Text>
                                        </div>
                                      </div>
                                      <Button
                                        type="primary"
                                        size="small"
                                        loading={applyingCertId === cert.id}
                                        onClick={() => handleApplyCert(cert.id)}
                                      >{t('Deploy')}</Button>
                                    </div>
                                  </Card>
                                ))}
                              </div>
                            )}
                            {incompatible.length > 0 && (
                              <div>
                                <Text type="secondary" style={{ marginBottom: 8, display: 'block', fontSize: 12 }}>{t('Mismatched certificate (domain mismatch)')}</Text>
                                {incompatible.map(cert => (
                                  <Card
                                    key={cert.id}
                                    size="small"
                                    style={{ marginBottom: 8, opacity: 0.5 }}
                                    styles={{ body: { padding: '8px 12px' } }}
                                  >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <div>
                                        <Text style={{ wordBreak: 'break-all' }}>{cert.domain}</Text>
                                        <div style={{ marginTop: 2 }}>
                                          <Text type="secondary" style={{ fontSize: 11 }}>{t('{{issuer}} · Expires {{value}}', { issuer: cert.issuer, value: (cert.expires_at || '').split(' ')[0] })}</Text>
                                        </div>
                                      </div>
                                      <Tooltip title={t('Certificate domains do not match this hostname')}>
                                        <Button size="small" disabled>{t('Does not match')}</Button>
                                      </Tooltip>
                                    </div>
                                  </Card>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )
                }
              ]}
            />
          </div>
        )}
      </Modal>

      {/* Origin CA Create Modal */}
      <Modal
        title={
          <Space>
            <SafetyCertificateOutlined />
            <span>{t('Issue Origin CA certificate')}</span>
          </Space>
        }
        open={originCertModalVisible}
        onCancel={() => { setOriginCertModalVisible(false); originCertForm.resetFields(); }}
        footer={null}
        width={isMobile ? '100%' : 600}
        style={isMobile ? { top: 20 } : undefined}
      >
        <Form
          form={originCertForm}
          layout="vertical"
          onFinish={handleCreateOriginCert}
          initialValues={{
            key_type: 'ecc256',
            validity_days: 5475
          }}
        >
          <Form.List name="hostnames">
            {(fields, { add, remove }) => (
              <Form.Item label={t('Domains (SANs)')}>
                {fields.map((field, index) => (
                  <div key={field.key} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <Form.Item
                      {...field}
                      style={{ flex: 1, marginBottom: 0 }}
                      rules={[{ required: true, message: t('Please enter a domain') }]}
                    >
                      <Input placeholder={t('example.com or *.example.com')} />
                    </Form.Item>
                    {fields.length > 1 && (
                      <Button
                        icon={<MinusCircleOutlined />}
                        onClick={() => remove(field.name)}
                        danger
                        type="text"
                      />
                    )}
                  </div>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add()}
                  icon={<PlusOutlined />}
                  block
                >{t('Add domain')}</Button>
              </Form.Item>
            )}
          </Form.List>

          <Form.Item name="key_type" label={t('Key type')}>
            <Select>
              <Select.Option value="ecc256">{t('ECC P-256 (recommended)')}</Select.Option>
              <Select.Option value="rsa2048">RSA 2048</Select.Option>
              <Select.Option value="rsa4096">RSA 4096</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="validity_days" label={t('Validity')}>
            <Select>
              <Select.Option value={7}>{t('7 days')}</Select.Option>
              <Select.Option value={30}>{t('30 days')}</Select.Option>
              <Select.Option value={90}>{t('90 days')}</Select.Option>
              <Select.Option value={365}>{t('1 year')}</Select.Option>
              <Select.Option value={1825}>{t('5 years')}</Select.Option>
              <Select.Option value={5475}>{t('15 years (recommended)')}</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SafetyCertificateOutlined />}
              loading={originCertCreateLoading}
              block
            >{t('Issue certificate')}</Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* View Certificate Modal */}
      <Modal
        title={
          <Space>
            <SafetyCertificateOutlined />
            <span>{t('Certificate details')}</span>
          </Space>
        }
        open={!!viewCertModal}
        onCancel={() => setViewCertModal(null)}
        footer={
          <Button onClick={() => setViewCertModal(null)}>{t('Close')}</Button>
        }
        width={isMobile ? '100%' : 700}
        style={isMobile ? { top: 20 } : undefined}
        styles={isMobile ? { body: { maxHeight: '70vh', overflow: 'auto' } } : undefined}
      >
        {viewCertModal && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>{t('Domain: ')}</Text>
              <Space size={4} wrap>
                {ensureArray(viewCertModal.hostnames).map(h => (
                  <Tag key={h}>{h}</Tag>
                ))}
              </Space>
            </div>

            {viewCertModal.serial_number && (
              <div style={{ marginBottom: 8 }}>
                <Text strong>{t('Serial number: ')}</Text>
                <Text code>{viewCertModal.serial_number}</Text>
              </div>
            )}

            <div style={{ marginBottom: 8 }}>
              <Text strong>{t('Validity: ')}</Text>
              <Text>{viewCertModal.issued_at} ~ {viewCertModal.expires_at}</Text>
            </div>

            <Divider />

            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong>{t('Certificate (PEM)')}</Text>
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(viewCertModal.certificate, t('Certificate'))}
                >{t('Copy')}</Button>
              </div>
              <TextArea
                value={viewCertModal.certificate}
                rows={6}
                readOnly
                style={{ fontFamily: 'monospace', fontSize: 11 }}
              />
            </div>

            {viewCertModal.private_key && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <Text strong>{t('Private key (PEM)')}</Text>
                  <Button
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(viewCertModal.private_key, t('Private key'))}
                  >{t('Copy')}</Button>
                </div>
                <Alert
                  message={t('Please keep your private key secure. You will not be able to view it again after closing this window.')}
                  type="warning"
                  showIcon
                  style={{ marginBottom: 8 }}
                />
                <TextArea
                  value={viewCertModal.private_key}
                  rows={6}
                  readOnly
                  style={{ fontFamily: 'monospace', fontSize: 11 }}
                />
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
